import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  CloseCircleOutline,
  ExclamationCircleOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import useDeletePartner from 'app/hooks/useDeletePartner';

import { DeletePartnerModalProps } from './types';

function DeletePartnerModal({ id, isOpen, onClose }: DeletePartnerModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: deletePartner, isLoading } = useDeletePartner({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Partner deleted',
        description: 'The partner was successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchPartnersPage'] });
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the partner could not be deleted. Please try again.',
      });
      onClose();
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <ExclamationCircleOutline color="system.error" boxSize="24px" />
            <Text as="h6">Delete partner?</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close delete partner"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text color="brand.mutedPurple" textStyle="body">
            This will permanently delete the partner. This action cannot be undone.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Spacer />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" isLoading={isLoading} onClick={() => deletePartner({ id })}>
            Yes, delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DeletePartnerModal;
